/**
 * Owns the antd Form instance for the Vehicle Request tab and keeps it seeded
 * from the vehicle being edited. Validation/assembly live in the modal so the
 * tab + inventory + VIN state can all participate; this hook just manages the
 * form lifecycle and value mapping.
 *
 * @module vehicleModal/hooks/useVehicleForm
 */
import { Form } from "antd";
import { useCallback, useEffect } from "react";
import { vehicleToFormValues } from "../utils/vehiclePayload";

/**
 * @param {Object} args
 * @param {boolean} args.open - modal visibility (re-seed/clear on transitions)
 * @param {import("../types").Vehicle|null} [args.initialVehicle] - edit source
 * @returns {{
 *   form: import("antd").FormInstance,
 *   resetForm: () => void,
 *   getValues: () => import("../types").VehicleFormValues,
 * }}
 */
export default function useVehicleForm({ open, initialVehicle }) {
  const [form] = Form.useForm();

  const resetForm = useCallback(() => {
    form.resetFields();
  }, [form]);

  // Seed (edit) or clear (add) whenever the modal opens or its target changes.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (initialVehicle) {
      form.setFieldsValue(vehicleToFormValues(initialVehicle));
    }
  }, [open, initialVehicle, form]);

  const getValues = useCallback(() => form.getFieldsValue(true), [form]);

  return { form, resetForm, getValues };
}
